/**
 * Retries transient QZ printer errors: `NullPointerException` while the backend
 * is still warming up, and `Failed to sign request` (e.g. stale signature after sleep).
 */

const DEFAULT_RETRIES = 5;
const DEFAULT_DELAY_MS = 500;
const DEFAULT_BACKOFF_FACTOR = 2;
const DEFAULT_MAX_DELAY_MS = 4_000;

const TRANSIENT_PRINTER_ERROR_PATTERN =
  /nullpointerexception|failed to sign request/i;

const messageOf = (err: unknown): string => {
  if (err instanceof Error) return err.message;

  if (typeof err === "string") return err;

  if (err && typeof err === "object" && "message" in err) {
    const value = (err as { message?: unknown }).message;
    return typeof value === "string" ? value : "";
  }
  return "";
};

export const isTransientPrinterError = (err: unknown): boolean =>
  TRANSIENT_PRINTER_ERROR_PATTERN.test(messageOf(err));

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export type RetryOnTransientErrorOptions = {
  backoffFactor?: number;
  delayMs?: number;
  maxDelayMs?: number;
  retries?: number;
  shouldRetry?: (err: unknown) => boolean;
};

/**
 * Run `fn`, retrying with capped exponential backoff while `shouldRetry`
 * (default {@link isTransientPrinterError}) holds and attempts remain. The last
 * error is rethrown once retries are exhausted, so callers see the real failure.
 */
export const retryOnTransientError = async <T>(
  fn: () => Promise<T>,
  {
    backoffFactor = DEFAULT_BACKOFF_FACTOR,
    delayMs = DEFAULT_DELAY_MS,
    maxDelayMs = DEFAULT_MAX_DELAY_MS,
    retries = DEFAULT_RETRIES,
    shouldRetry = isTransientPrinterError,
  }: RetryOnTransientErrorOptions = {},
): Promise<T> => {
  let attempt = 0;

  for (;;) {
    try {
      // Retries are inherently sequential: each attempt waits on the previous.
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      if (attempt >= retries || !shouldRetry(err)) throw err;

      // eslint-disable-next-line no-await-in-loop
      await wait(Math.min(delayMs * backoffFactor ** attempt, maxDelayMs));
      attempt += 1;
    }
  }
};
